import { roundMoney } from "./pricing";
import { groupPlates, liveOpenTotals, type PlateGroup } from "./groupPlates";
import { allocateInvoiceRefundShares } from "./refundAllocation";
import {
  allocationBasis,
  markablePlates,
  outstandingOnBill,
  partyShares,
} from "./partyShares";
import type { InvoiceCommand } from "@/shared/invoices/invoiceCommands";
import { tenderReferenceField } from "./tenderReference";
import type { Invoice, InvoiceTender, SettlementMethod } from "@/lib/api/types";

/**
 * THE PAPER SETTLE SHEET (founder, 2026-10-03): "pay in full and by item — no
 * split evenly — and keep InstaPay".
 *
 * Every figure and every command here is one the till ALREADY has. Nothing in
 * this file is a money formula of its own:
 *
 *  · **Pay in full** is the whole-bill `settleInvoice` on a bill nobody has paid
 *    toward yet, and `settlePartial` for "the rest" on one somebody has (the
 *    server refuses a whole-bill settle on a part-paid bill — B43's
 *    double-charge fence — and the till has always routed that case to the
 *    split-tender path). Its amount is EDITABLE: less than what is owed is a
 *    part payment — `settlePartial` with that amount (B3, "the guest pays 200
 *    now", card for part and cash for the rest) — and the window stays open
 *    on the new remainder for the next tender.
 *  · **By item** is B43's party settlement: the chosen plates become a PARTY
 *    (`assignParty`) and that party is settled (`settlePartial` with a
 *    `partyId` and NO amount — the server derives it). The figure the sheet
 *    quotes is the same allocation the server derives it with
 *    (`allocateInvoiceRefundShares` over the WHOLE chargeable set, the chosen
 *    plates summed out of it), which is exactly how `partyShares` quotes a
 *    family on the split sheets.
 *  · A settle needs a CLOSED bill (both settle commands refuse an OPEN one), so
 *    on an open bill the sheet closes it first, through `closeInvoice`, on the
 *    version the till observed — the same command and guard the Close button
 *    sends.
 *
 * "Split evenly" is not built: the founder declined it, and B43 already decided
 * against an equal-share PAYMENT path (the pre-bill check prints a split as
 * arithmetic only).
 */

export type SettleMode = "full" | "items";

/** What the sheet says is still owed — its big "Remaining" figure. */
export function settleRemaining(invoice: Invoice): number {
  if (invoice.status === "SETTLED" || invoice.status === "CANCELLED") return 0;
  if (invoice.status === "OPEN") {
    /*
     * The live total the order panel already quotes (`liveOpenTotals`, the
     * client twin of the close's `calculateInvoiceTotals`), less anything taken
     * toward it — a part-paid bill can be reopened. The same subtraction the
     * floor's `amountDue` makes on an open bill.
     */
    return Math.max(
      0,
      roundMoney(liveOpenTotals(invoice).total - invoice.tenderedTotal),
    );
  }
  return outstandingOnBill(invoice);
}

/**
 * The plates whose money is IN — the ones the panel may tag "Paid".
 *
 * The model says so for exactly one case: a plate marked to a party that is
 * square (`partyShares`' `isSettled`, off the ledger). A typed part-payment
 * names no plate, so it marks none; and a party paid part of its share has no
 * plate the till could honestly call paid.
 */
export function paidPlateIds(invoice: Invoice): Set<string> {
  const settled = new Set(
    partyShares(invoice)
      .filter((share) => share.isSettled)
      .map((share) => share.id),
  );
  return new Set(
    markablePlates(invoice)
      .filter((item) => item.partyId !== null && settled.has(item.partyId))
      .map((item) => item.id),
  );
}

/** One row of the "by item" list: a group of identical unpaid plates. */
export type SettleRow = {
  key: string;
  group: PlateGroup;
  /** The plates a tap can take, oldest first — a stepper takes the first N. */
  plateIds: string[];
  /** Each plate's share of the bill, charges included, in `plateIds` order. */
  shares: (number | null)[];
  /** What the whole row comes to, or `null` when the till cannot say. */
  total: number | null;
  /** One plate's share when every plate in the row shares it, else `null`. */
  each: number | null;
};

/**
 * The plates a guest can pay for by item, grouped the way the order panel
 * groups them.
 *
 * Every chargeable plate EXCEPT those of a party that has already handed money
 * over: a square party's plates are paid, and a party paid part of its share
 * cannot have a plate taken out from under it without moving money it already
 * gave. Plates marked to a party nobody has paid for yet stay pickable — that is
 * a waiter's mark, not money.
 */
export function settleRows(
  invoice: Invoice,
  money?: (value: number) => string,
): SettleRow[] {
  const plates = markablePlates(invoice);
  const shares = allocateInvoiceRefundShares(
    allocationBasis(invoice),
    plates.map((item) => ({ id: item.id, lineTotal: item.lineTotal })),
  );
  const paying = new Set(
    partyShares(invoice)
      .filter((share) => share.paid > 0)
      .map((share) => share.id),
  );
  const open = plates.filter(
    (item) => item.partyId === null || !paying.has(item.partyId),
  );
  return groupPlates(open, money).map((group) => {
    const rowShares = group.plates.map((plate) =>
      shares ? (shares.get(plate.id)?.amount ?? 0) : null,
    );
    const total = shares
      ? rowShares.reduce<number>(
          (sum, share) => roundMoney(sum + (share ?? 0)),
          0,
        )
      : null;
    const first = rowShares[0] ?? null;
    return {
      key: group.key,
      group,
      plateIds: group.plates.map((plate) => plate.id),
      shares: rowShares,
      total,
      each:
        first !== null && rowShares.every((share) => share === first)
          ? first
          : null,
    };
  });
}

/** How many of each row the cashier has picked, keyed by `SettleRow.key`. */
export type SettleSelection = Readonly<Record<string, number>>;

/** The plate ids a selection takes — the first N of each row. */
export function selectedPlateIds(
  rows: readonly SettleRow[],
  selection: SettleSelection,
): string[] {
  return rows.flatMap((row) =>
    row.plateIds.slice(0, clampCount(selection[row.key], row.plateIds.length)),
  );
}

/** How many plates a selection takes. */
export function selectedCount(
  rows: readonly SettleRow[],
  selection: SettleSelection,
): number {
  return rows.reduce(
    (sum, row) => sum + clampCount(selection[row.key], row.plateIds.length),
    0,
  );
}

/**
 * What the picked plates come to — summed in the same order and with the same
 * rounding the server's `amountOwedByParty` sums a party's plates, so the
 * quote and the tender cannot disagree. `null` when the till cannot state the
 * split (the server is the authority either way).
 */
export function selectionAmount(
  rows: readonly SettleRow[],
  selection: SettleSelection,
): number | null {
  let amount = 0;
  for (const row of rows) {
    const count = clampCount(selection[row.key], row.plateIds.length);
    for (let index = 0; index < count; index += 1) {
      const share = row.shares[index];
      if (share === null || share === undefined) return null;
      amount = roundMoney(amount + share);
    }
  }
  return amount;
}

function clampCount(value: number | undefined, max: number): number {
  if (!value || value < 0) return 0;
  return Math.min(Math.floor(value), max);
}

/**
 * The label a by-item payment's party is created under: the next "Guest N"
 * nobody on this bill has used. The server finds-or-creates a party BY LABEL,
 * so a reused label would fold this guest into an earlier one.
 */
export function nextPartyLabel(
  invoice: Pick<Invoice, "parties">,
  word: (number: number) => string,
): string {
  const taken = new Set(invoice.parties.map((party) => normalizeLabel(party.label)));
  for (let number = invoice.parties.length + 1; ; number += 1) {
    const label = word(number);
    if (!taken.has(normalizeLabel(label))) return label;
  }
}

function normalizeLabel(label: string): string {
  return label.trim().replace(/\s+/g, " ").toLowerCase();
}

/**
 * An existing party whose chargeable plates are EXACTLY these, with nothing
 * paid yet — the waiter already marked this guest's plates on the split sheet,
 * so the payment is taken against that party rather than re-marking the same
 * plates onto a new one.
 */
export function matchingParty(
  invoice: Invoice,
  plateIds: readonly string[],
): string | null {
  const wanted = new Set(plateIds);
  const plates = markablePlates(invoice);
  for (const share of partyShares(invoice)) {
    if (share.paid > 0) continue;
    const mine = plates.filter((item) => item.partyId === share.id);
    if (
      mine.length === wanted.size &&
      mine.every((item) => wanted.has(item.id))
    ) {
      return share.id;
    }
  }
  return null;
}

/**
 * The tenders the sheet offers, in the design's order (Card, Cash) with the
 * till's two conditional ones around them — and InstaPay ALWAYS listed (founder:
 * "we need to keep InstaPay").
 *
 * · `AGGREGATOR` only on a bill a platform brought, and only for a whole-bill
 *   settle — the rule `SettleSheet` applies (F2/B106) and the split-tender
 *   sheet's (it never offers a platform for part of a bill).
 * · `INSTAPAY` is listed every time but TAKEN only once a transfer for this
 *   bill has been confirmed by staff (D44): "a permanent third pill would
 *   invite closing an unpaid bill as 'transfer' on nothing but the customer's
 *   word", and a transfer is revenue in neither drawer, so a wrong one never
 *   shows up at the count.
 * · WRITE_OFF is not a tender. It stays the separate, two-tap act on the void
 *   rung (D31), outside this list.
 */
export type SettleMethodOption = {
  method: SettlementMethod;
  enabled: boolean;
};

export function settleMethodOptions(input: {
  mode: SettleMode;
  hasPlatform: boolean;
  transferConfirmed: boolean;
  /** Money already taken toward the bill. */
  tendered: number;
}): SettleMethodOption[] {
  const options: SettleMethodOption[] = [];
  if (input.hasPlatform && input.mode === "full" && input.tendered <= 0) {
    options.push({ method: "AGGREGATOR", enabled: true });
  }
  options.push(
    { method: "CARD", enabled: true },
    { method: "CASH", enabled: true },
    { method: "INSTAPAY", enabled: input.transferConfirmed },
  );
  return options;
}

/**
 * The tender the sheet opens on — `SettleSheet`'s rule, unchanged: the
 * platform on a platform order (the money is already with it), a confirmed
 * transfer next, and cash otherwise.
 */
export function initialSettleMethod(input: {
  hasPlatform: boolean;
  transferConfirmed: boolean;
}): SettlementMethod {
  if (input.hasPlatform) return "AGGREGATOR";
  if (input.transferConfirmed) return "INSTAPAY";
  return "CASH";
}

/** The chosen tender, or cash when this mode does not offer it. */
export function effectiveSettleMethod(
  chosen: SettlementMethod,
  options: readonly SettleMethodOption[],
): SettlementMethod {
  return options.some((option) => option.method === chosen && option.enabled)
    ? chosen
    : "CASH";
}

/** What one Pay tap asks for. */
export type SettlePlan =
  | {
      kind: "full";
      method: SettlementMethod;
      /**
       * What this tender takes — `null` is "exactly what is owed".
       *
       * BELOW what the closed bill still owes it is a PART payment (B3: "the
       * guest pays 200 now", or card for part and cash for the rest): one
       * `settlePartial` with that amount, the bill stays CLOSED and the window
       * stays open on the new remainder for the next tender. At or above it,
       * it finishes the bill — and for CASH the figure is what was handed
       * over, so the change survives (D26); any other tender is charged what
       * is owed exactly.
       */
      amount: number | null;
      /**
       * D133 fix — the diner's claim this tender takes, when the window was
       * opened from its "Settle this share". Rides the money command only
       * (never the close or the party mark): the server stamps the claim and
       * verifies it when the tender is the claim unchanged.
       */
      dinerPaymentId?: string;
      /**
       * D163 — what the cashier typed in «مرجع العملية», if anything. Sent
       * only with CARD/INSTAPAY and only when not blank (`tenderReferenceField`).
       */
      reference?: string;
    }
  | {
      kind: "items";
      method: SettlementMethod;
      plateIds: string[];
      /** The label a NEW party is created under (see `nextPartyLabel`). */
      partyLabel: string;
      /** D133 fix — as on the full plan. */
      dinerPaymentId?: string;
      /** D163 — as on the full plan. */
      reference?: string;
    }
  | { kind: "writeOff" };

/**
 * Sends one command; `ok: false` when it was refused (the caller has already
 * said why), and `invoice: null` when it landed but the bill is not readable
 * from the result.
 */
export type SendSettleCommand = (
  command: InvoiceCommand,
  key: string,
) => Promise<{ ok: false } | { ok: true; invoice: Invoice | null }>;

export type SettleOutcome =
  | { status: "done"; invoice: Invoice }
  /** Stopped on a refusal or an unreadable result; the bill is as the server left it. */
  | { status: "stopped" };

/**
 * One Pay tap, as the sequence of commands the till already sends.
 *
 * Every command goes through `send` — the page's own dispatch, which mints the
 * UUIDv7 idempotency key, retries a lost response on the same id and surfaces
 * a refusal — and each guarded command carries the version the PREVIOUS step
 * returned, because a close and an `assignParty` both move it.
 */
/** D133 fix — the claim id (and D163 the reference) as payload fields, or nothing. */
function claimField(
  plan: SettlePlan,
): { dinerPaymentId?: string; reference?: string } {
  if (plan.kind === "writeOff") return {};
  return {
    ...(plan.dinerPaymentId ? { dinerPaymentId: plan.dinerPaymentId } : {}),
    // D163 — the tender's reference rides the money command only, like the
    // claim: never the close or the party mark.
    ...tenderReferenceField(plan.method, plan.reference),
  };
}

export async function runSettle(
  invoice: Invoice,
  plan: SettlePlan,
  send: SendSettleCommand,
  options: {
    /**
     * PAY WITHOUT CLOSING (D126, founder 2026-10-07): something on the bill is
     * still unsent or waiting at the kitchen, so the money is taken against the
     * OPEN bill — no close, which would pull cooking dishes off the kitchen
     * board. The bill is closed later, as usual.
     */
    keepOpen?: boolean;
    /** What is owed now, as the sheet priced it — an open bill has no stored total yet. */
    liveDue?: number;
  } = {},
): Promise<SettleOutcome> {
  let current = invoice;
  const keepOpen = Boolean(options.keepOpen) && current.status === "OPEN";

  if (current.status === "OPEN" && !keepOpen) {
    const closed = await send(
      {
        type: "closeInvoice",
        invoiceId: current.id,
        payload: { expectedVersion: current.version },
      },
      "close",
    );
    if (!closed.ok || !closed.invoice) return { status: "stopped" };
    current = closed.invoice;
  }
  if (current.status !== "CLOSED" && !keepOpen) return { status: "stopped" };

  if (plan.kind === "writeOff") {
    // A walk-out is the WHOLE bill and needs it closed; never on an open one.
    if (keepOpen) return { status: "stopped" };
    return finish(
      await send(
        {
          type: "settleInvoice",
          invoiceId: current.id,
          payload: {
            expectedVersion: current.version,
            settlementMethod: "WRITE_OFF",
            settlementAmount: 0,
          },
        },
        "settle",
      ),
    );
  }

  if (plan.kind === "full" && keepOpen) {
    // Always a tender on an open bill: `settleInvoice` needs it closed.
    const due = roundMoney(options.liveDue ?? 0);
    const typed = plan.amount === null ? null : roundMoney(plan.amount);
    if (typed !== null && !(typed > 0)) return { status: "stopped" };
    if (!(due > 0) && typed === null) return { status: "stopped" };
    const settlementAmount =
      typed !== null && (typed < due || plan.method === "CASH") ? typed : due;
    return finish(
      await send(
        {
          type: "settlePartial",
          invoiceId: current.id,
          payload: {
            expectedVersion: current.version,
            settlementMethod: plan.method,
            settlementAmount,
            ...claimField(plan),
          },
        },
        "partial",
      ),
    );
  }

  if (plan.kind === "full") {
    const partPaid = current.tenderedTotal > 0;
    // The SERVER's figure now that the bill is closed — never the live quote
    // the sheet showed a moment ago.
    const due = partPaid ? outstandingOnBill(current) : current.total;
    const typed = plan.amount === null ? null : roundMoney(plan.amount);
    if (typed !== null && !(typed > 0)) return { status: "stopped" };
    if (typed !== null && typed < due) {
      /*
       * A PART of the bill — the split-tender path `PartialSettleSheet` takes
       * on every other look (B3). The server caps nothing here because nothing
       * is over; the bill settles itself when the tenders reach its total.
       */
      return finish(
        await send(
          {
            type: "settlePartial",
            invoiceId: current.id,
            payload: {
              expectedVersion: current.version,
              settlementMethod: plan.method,
              settlementAmount: typed,
              ...claimField(plan),
            },
          },
          "partial",
        ),
      );
    }
    // The whole of what is owed. Cash keeps what was handed over (change);
    // every other tender is charged the owed figure exactly.
    const settlementAmount =
      plan.method === "CASH" && typed !== null ? typed : due;
    return finish(
      await send(
        partPaid
          ? {
              type: "settlePartial",
              invoiceId: current.id,
              payload: {
                expectedVersion: current.version,
                settlementMethod: plan.method,
                settlementAmount,
                ...claimField(plan),
              },
            }
          : {
              type: "settleInvoice",
              invoiceId: current.id,
              payload: {
                expectedVersion: current.version,
                settlementMethod: plan.method,
                settlementAmount,
                ...claimField(plan),
              },
            },
        partPaid ? "partial" : "settle",
      ),
    );
  }

  let partyId = matchingParty(current, plan.plateIds);
  if (!partyId) {
    const marked = await send(
      {
        type: "assignParty",
        invoiceId: current.id,
        payload: { itemIds: [...plan.plateIds], label: plan.partyLabel },
      },
      "party",
    );
    if (!marked.ok || !marked.invoice) return { status: "stopped" };
    current = marked.invoice;
    const wanted = normalizeLabel(plan.partyLabel);
    partyId =
      current.parties.find((party) => normalizeLabel(party.label) === wanted)
        ?.id ?? null;
    if (!partyId) return { status: "stopped" };
  }
  return finish(
    await send(
      {
        type: "settlePartial",
        invoiceId: current.id,
        // NO amount: the server derives the party's share, and a figure sent
        // beside it could only ever disagree with it.
        payload: {
          expectedVersion: current.version,
          settlementMethod: plan.method,
          partyId,
          ...claimField(plan),
        },
      },
      "partial",
    ),
  );
}

function finish(
  result: { ok: false } | { ok: true; invoice: Invoice | null },
): SettleOutcome {
  return result.ok && result.invoice
    ? { status: "done", invoice: result.invoice }
    : { status: "stopped" };
}

/** The money one finished Pay took: the ledger's own tendered total, before and after. */
export function amountTaken(before: Invoice, after: Invoice): number {
  return Math.max(0, roundMoney(after.tenderedTotal - before.tenderedTotal));
}

/**
 * The payments the sheet lists — `Invoice.tenders` (B117), in the order the
 * money arrived. A bill settled before the ledger existed has none, and reads
 * its stored method and total instead, exactly as every bill-level screen does;
 * a write-off took no money and lists nothing.
 */
export function settlePayments(invoice: Invoice): InvoiceTender[] {
  if (invoice.tenders.length > 0) return invoice.tenders;
  if (
    invoice.status === "SETTLED" &&
    invoice.settlementMethod &&
    invoice.settlementMethod !== "WRITE_OFF"
  ) {
    return [{ method: invoice.settlementMethod, amount: invoice.total }];
  }
  return [];
}
